import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ProjectAssetsPanel from '../components/ProjectAssetsPanel';
import ChatPanel from '../components/chat/ChatPanel';
import ChatRail from '../components/chat/ChatRail';
import VerboseDrawer from '../components/chat/VerboseDrawer';
import BuildsDialog from '../components/BuildsDialog';
import VersionControlPanel from '../components/versions/VersionControlPanel';
import Sidebar from '../components/Sidebar';
import { Project, DisplayMode } from '../models/project';
import type { ChatListItem } from '../models/chat';
import { getProject, updateProject, cancelProject, buildProject } from '../services/projects';
import { getAvailablePlatforms, getProjectBuilds, PlatformBuildInfo, TargetPlatform } from '../services/builds';
import { getProfile, Profile as ProfileType } from '../services/profile';
import { ENGINE_UPGRADE_REQUEST, isEngineUpgradeRequiredError } from '../services/engineUpgrade';
import ComingSoonPopup from '../components/ComingSoonPopup';
import InlineError from '../components/InlineError';
import OutOfCreditsPopup from '../components/OutOfCreditsPopup';
import ReconnectingIndicator from '../components/ReconnectingIndicator';
import { actionFailureMessage } from '../utils/actionFailure';
import type {
  UsageUpdate,
  MilestoneMessage,
  VerboseLogMessage,
  BuildStateMessage,
  SystemMessageMessage,
} from '../models/pipelineEvents';
import { PipelineEventsProvider, usePipelineEvents } from '../context/PipelineEventsContext';
import { useChats } from '../hooks/useChats';
import { useFreshnessCheck } from '../hooks/useFreshnessCheck';
import { useOrganizeJob } from '../hooks/useOrganizeJob';
import { useVersions } from '../hooks/useVersions';
import { useIsMobile } from '../hooks/useIsMobile';
import { IconArrowL, IconPackage, IconGithub, IconPlay, IconCog, IconChat, IconFolder } from '../components/Icons';

const getDisplayModeName = (displayMode: DisplayMode | undefined): string => {
  if (!displayMode) return '';
  switch (displayMode) {
    case 'MobilePortrait': return 'Mobile Portrait';
    case 'MobileLandscape': return 'Mobile Landscape';
    case 'PC': return 'PC';
    default: return displayMode;
  }
};

const initialProject: Project = {
  id: '',
  name: 'New project',
  version: '0.0.0',
  platform: 'Windows',
  type: '2D',
};

export default function ProjectDashboard() {
  const { id } = useParams<{ id: string }>();
  return (
    <PipelineEventsProvider projectId={id || ''}>
      <ProjectDashboardPage />
      <ReconnectingIndicator />
    </PipelineEventsProvider>
  );
}

function ProjectDashboardPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['common']);
  const isMobile = useIsMobile();
  const [project, setProject] = useState<Project>(initialProject);
  const [showBuildsDialog, setShowBuildsDialog] = useState(false);
  const [showComingSoonPopup, setShowComingSoonPopup] = useState<null | string>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelNotice, setCancelNotice] = useState<string | null>(null);
  const [availablePlatforms, setAvailablePlatforms] = useState<TargetPlatform[]>([]);
  const [builds, setBuilds] = useState<Record<string, PlatformBuildInfo>>({});
  const [buildsLoading, setBuildsLoading] = useState(false);
  const [showOutOfCreditsPopup, setShowOutOfCreditsPopup] = useState(false);
  const [isVerboseDrawerOpen, setIsVerboseDrawerOpen] = useState(false);
  const [chatsRailCollapsed, setChatsRailCollapsed] = useState(false);
  const [mobileTab, setMobileTab] = useState<'chat' | 'assets'>('chat');
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const [profile, setProfile] = useState<ProfileType | null>(null);
  const [mainTab, setMainTab] = useState<'workspace' | 'versions'>('workspace');

  const chats = useChats(id || '');
  const organizeJob = useOrganizeJob(id || '');
  const organizing = organizeJob.state.phase === 'running';
  const versions = useVersions(id || '', (result) => {
    setProject((prev) => ({
      ...prev,
      version: result.semver,
      supportedDisplayModes: result.supportedDisplayModes,
      thumbnailUrl: result.thumbnailUrl ?? prev?.thumbnailUrl,
    }));
  });

  const activeVersion = versions.versions.find((v) => v.isActive);
  const activeSemver = activeVersion?.semver ?? project.version;
  const engineUpgradeRequired = activeVersion?.engineUpgradeRequired ?? project.engineUpgradeRequired ?? false;

  useEffect(() => {
    getProfile().then(setProfile).catch(() => {});
  }, []);

  const updateName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRenameError(null);
    setProject({ ...project, name: e.target.value });
  };

  const saveProjectName = async () => {
    if (!id) return;
    setRenameError(null);
    try {
      await updateProject(id, { name: project.name });
    } catch (err) {
      setRenameError(actionFailureMessage(err, 'Failed to save the project name.'));
    }
  };

  const loadAvailablePlatforms = useCallback(async () => {
    if (!id) return;
    try {
      const platforms = await getAvailablePlatforms(id);
      setAvailablePlatforms(platforms);
    } catch {
      setAvailablePlatforms([]);
    }
  }, [id]);

  const refreshBuilds = useCallback(async () => {
    if (!id) return;
    setBuildsLoading(true);
    try {
      const data = await getProjectBuilds(id);
      const map: Record<string, PlatformBuildInfo> = {};
      data.builds.forEach((b) => {
        map[b.platform] = b;
      });
      setBuilds(map);
      if (data.projectVersion) {
        setProject((prev) => ({ ...prev, version: data.projectVersion }));
      }
    } catch {
      // no-op
    } finally {
      setBuildsLoading(false);
    }
  }, [id]);

  const refreshProjectDetails = useCallback(async () => {
    if (!id) return;
    try {
      const projectData = await getProject(id);
      setProject((prev) => ({
        ...prev,
        thumbnailUrl: projectData.thumbnailUrl,
        creditsUsed: projectData.creditsUsed,
        totalGeneratedImages: projectData.totalGeneratedImages,
      }));
    } catch {
      // no-op
    }
  }, [id]);

  useEffect(() => {
    loadAvailablePlatforms();
  }, [loadAvailablePlatforms]);

  useEffect(() => {
    refreshBuilds();
  }, [refreshBuilds]);

  const handleCancelProject = async () => {
    if (!id) return;
    setIsCancelling(true);
    try {
      await cancelProject(id);
      // Keep showing "Cancelling…" until the server confirms the job finished
      // (currentJobId clears via ReceiveAssistantMessageReady) — see effect below.
    } catch (err) {
      setCancelNotice(actionFailureMessage(err, 'Couldn’t cancel the job. Please try again.'));
      setIsCancelling(false);
    }
  };

  const isJobRunning = chats.isCurrentRunning;
  useEffect(() => {
    if (!isJobRunning) {
      setIsCancelling(false);
    }
  }, [isJobRunning]);

  useEffect(() => {
    if (!cancelNotice) return;
    const timer = setTimeout(() => setCancelNotice(null), 4000);
    return () => clearTimeout(timer);
  }, [cancelNotice]);

  const handleUsage = useCallback((data: UsageUpdate) => {
    setProject((prev) => ({
      ...prev,
      creditsUsed: data.creditsUsed,
      totalGeneratedImages: data.generatedImages,
    }));
  }, []);

  const handleMilestone = useCallback((m: MilestoneMessage) => {
    chats.handleMilestone({
      id: m.milestoneId ?? `pushed:${m.jobId}:${m.timestamp}:${m.milestoneText}`,
      chatId: m.chatId,
      jobId: m.jobId,
      text: m.milestoneText,
      createdAt: m.createdAt ?? m.timestamp,
    });
  }, [chats]);

  const handleVerboseLog = useCallback((v: VerboseLogMessage) => {
    chats.handleVerboseLog(v.chatId, v.level, v.message, v.timestamp);
  }, [chats]);

  const refreshVersions = versions.refresh;
  const resyncChats = chats.resync;
  const readChangedChats = chats.readChangedChats;
  const recheckOrganizing = organizeJob.recheck;

  const resync = useCallback((list?: ChatListItem[]) => {
    void resyncChats({ list, refreshAlongside: refreshVersions });
    void refreshBuilds();
    void refreshProjectDetails();
    void loadAvailablePlatforms();
  }, [resyncChats, refreshVersions, refreshBuilds, refreshProjectDetails, loadAvailablePlatforms]);

  const checkFreshness = useCallback(async () => {
    if (organizing) recheckOrganizing();
    const changed = await readChangedChats();
    if (changed) resync(changed);
  }, [organizing, recheckOrganizing, readChangedChats, resync]);

  useFreshnessCheck(checkFreshness, { pollWhile: chats.isAnyRunning || organizing });

  const handleSystemMessage = useCallback((m: SystemMessageMessage) => {
    chats.handleSystemMessage(m.message);
    void resyncChats({ refreshAlongside: refreshVersions });
  }, [chats, resyncChats, refreshVersions]);

  const handleStartBuild = useCallback(async (platform: TargetPlatform) => {
    if (!id) return;
    let previous: PlatformBuildInfo | undefined;
    setBuilds((prev) => {
      previous = prev[platform];
      return {
        ...prev,
        [platform]: {
          platform,
          state: 'Building',
          version: previous?.version ?? null,
          sizeBytes: previous?.sizeBytes ?? null,
          errorMessage: null,
        },
      };
    });
    try {
      await buildProject(id, { buildPlatform: platform });
    } catch (err) {
      setBuilds((prev) => {
        const next = { ...prev };
        if (previous) {
          next[platform] = previous;
        } else {
          delete next[platform];
        }
        return next;
      });
      if (!isEngineUpgradeRequiredError(err)) throw err;
      refreshVersions();
    }
  }, [id, refreshVersions]);

  const startChat = chats.startChat;
  const handleUpgradeEngine = useCallback(() => {
    setShowBuildsDialog(false);
    setMainTab('workspace');
    setMobileTab('chat');
    void startChat(ENGINE_UPGRADE_REQUEST);
  }, [startChat]);

  const onVersionBuildState = versions.onBuildState;
  const handleBuildStateChanged = useCallback((msg: BuildStateMessage) => {
    if (msg.purpose === 'Published') {
      onVersionBuildState(msg);
      return;
    }
    setBuilds((prev) => ({
      ...prev,
      [msg.platform]: {
        platform: msg.platform,
        state: msg.state,
        version: msg.version,
        sizeBytes: msg.sizeBytes,
        errorMessage: msg.errorMessage,
      },
    }));
    if (msg.version) setProject((prev) => ({ ...prev, version: msg.version as string }));
    if (msg.state === 'Ready' || msg.state === 'Failed') loadAvailablePlatforms();
  }, [loadAvailablePlatforms, onVersionBuildState]);

  usePipelineEvents({
    onReconnected: () => resync(),
    onUsage: handleUsage,
    onMilestone: handleMilestone,
    onVerboseLog: handleVerboseLog,
    onAssistantMessageReady: () => resync(),
    onSystemMessage: handleSystemMessage,
    onBuildStateChanged: handleBuildStateChanged,
  });

  useEffect(() => {
    const loadProject = async () => {
      if (!id) {
        setLoadError('Project ID not found');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const projectData = await getProject(id);

        const metadataKey = `project_${id}_metadata`;
        const savedMetadata = localStorage.getItem(metadataKey);
        let projectType: '2D' | '3D' = '2D';
        let projectPlatform: 'Windows' | 'Android' | 'iOS' | 'Mac' | 'Web' = 'Windows';
        if (savedMetadata) {
          try {
            const metadata = JSON.parse(savedMetadata);
            projectType = metadata.type || '2D';
            projectPlatform = metadata.platform || 'Windows';
          } catch {
            // ignore
          }
        }

        setProject({
          id: projectData.id,
          name: projectData.name,
          version: projectData.version,
          platform: projectPlatform,
          type: projectType,
          creditsUsed: projectData.creditsUsed,
          totalGeneratedImages: projectData.totalGeneratedImages,
          supportedDisplayModes: projectData.supportedDisplayModes,
          thumbnailUrl: projectData.thumbnailUrl,
          engineUpgradeRequired: projectData.engineUpgradeRequired,
        });
      } catch {
        setLoadError('Failed to load project data');
      } finally {
        setIsLoading(false);
      }
    };

    loadProject();
  }, [id]);

  if (isLoading) {
    return (
      <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', background: 'var(--bg)' }}>
        <div className="muted">{t('dashboard.loading', 'Loading project…')}</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', background: 'var(--bg)' }}>
        <div
          style={{
            padding: '12px 18px',
            border: '1px solid var(--danger-line)',
            background: 'var(--danger-wash)',
            color: 'var(--danger)',
            borderRadius: 10,
          }}
        >
          {loadError}
        </div>
      </div>
    );
  }

  const isWebGLReady = availablePlatforms.includes('WebGL');
  const displayMode = project.supportedDisplayModes?.[0];
  const buildsCount = Object.keys(builds).length;

  return (
    <div style={{ display: 'flex', height: '100dvh', background: 'var(--bg)', color: 'var(--text)' }}>
      <Sidebar
        active="projects"
        credits={profile?.credits.remainingCredits}
        creditsMax={profile?.credits.totalCredits}
        projectsUsed={profile?.credits.currentProjectCount}
        projectsMax={profile?.credits.maxProjects}
        defaultCollapsed
        onCommunityClick={() => setShowComingSoonPopup('Community')}
        onSettingsClick={() => setShowComingSoonPopup('Settings')}
        onBillingClick={() => setShowComingSoonPopup('Credits & billing')}
      />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Project header */}
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            padding: isMobile ? '0 14px' : '0 28px',
            borderBottom: '1px solid var(--line)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 14, minWidth: 0 }}>
            <Link to="/profile" className="nav-link" style={{ padding: '0 6px' }} title={t('dashboard.backToProjects', 'Back to projects')}>
              <IconArrowL />
            </Link>
            {project.thumbnailUrl ? (
              <img
                src={project.thumbnailUrl}
                alt={project.name}
                style={{
                  width: isMobile ? 32 : 44,
                  height: isMobile ? 32 : 44,
                  borderRadius: 10,
                  objectFit: 'cover',
                  border: '1px solid var(--line)',
                  flexShrink: 0,
                }}
              />
            ) : (
              <div
                style={{
                  width: isMobile ? 32 : 44,
                  height: isMobile ? 32 : 44,
                  borderRadius: 10,
                  background: 'linear-gradient(135deg, #7A4A3B, #15151A 120%)',
                  border: '1px solid var(--line)',
                  flexShrink: 0,
                }}
              />
            )}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, minWidth: 0 }}>
              <input
                value={project.name}
                onChange={updateName}
                onBlur={saveProjectName}
                className="display"
                style={{
                  fontSize: isMobile ? 16 : 22,
                  fontWeight: 600,
                  letterSpacing: '-0.02em',
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: 'var(--text)',
                  padding: 0,
                  width: `${Math.max(project.name.length + 1, 8)}ch`,
                  maxWidth: isMobile ? '40vw' : undefined,
                }}
              />
              {!isMobile && (
                <div style={{ fontSize: 12, color: 'var(--text-5)' }}>
                  v{project.version} · {project.type}
                  {displayMode ? ` · ${getDisplayModeName(displayMode)}` : ''}
                </div>
              )}
              {renameError && <InlineError>{renameError}</InlineError>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button type="button" className={isMobile ? 'btn sm icon' : 'btn sm'} onClick={() => { setShowBuildsDialog(true); refreshBuilds(); }} title={t('dashboard.builds', 'Builds')}>
              <IconPackage size={14} />
              {!isMobile && t('dashboard.builds', 'Builds')}
              {buildsCount > 0 && <span className="mono muted" style={{ marginLeft: 6 }}>{buildsCount}</span>}
            </button>
            {!isMobile && (
              <button type="button" className="btn sm" onClick={() => setShowComingSoonPopup('Export to GitHub')}>
                <IconGithub size={14} />
                {t('dashboard.export', 'Export')}
              </button>
            )}
            <button
              type="button"
              className={isMobile ? 'btn primary sm icon' : 'btn primary sm'}
              onClick={() => isWebGLReady && id && navigate(`/play/${id}`)}
              disabled={!isWebGLReady}
              title={isWebGLReady ? t('dashboard.play', 'Play') : t('dashboard.notReady', 'Build a WebGL version first')}
            >
              <IconPlay size={14} />
              {!isMobile && (activeSemver ? `${t('dashboard.play', 'Play')} v${activeSemver}` : t('dashboard.play', 'Play'))}
            </button>
          </div>
        </div>

        {/* Main tabs */}
        <div style={{ display: 'flex', gap: 4, padding: isMobile ? '8px 14px' : '8px 28px', borderBottom: '1px solid var(--line)' }}>
          <button type="button" className={mainTab === 'workspace' ? 'btn primary sm' : 'btn sm'} onClick={() => setMainTab('workspace')}>
            {t('dashboard.tabs.workspace', 'Workspace')}
          </button>
          <button type="button" className={mainTab === 'versions' ? 'btn primary sm' : 'btn sm'} onClick={() => setMainTab('versions')}>
            {t('dashboard.tabs.versions', 'Version control')}
          </button>
        </div>

        {/* Body */}
        {mainTab === 'versions' ? (
          <VersionControlPanel projectId={id || ''} versions={versions} isChatRunning={chats.isAnyRunning} />
        ) : isMobile ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 14px',
                borderBottom: '1px solid var(--line)',
              }}
            >
              <button type="button" className="btn ghost sm" onClick={() => setChatDrawerOpen(true)} title={t('dashboard.tabs.chatsButton', 'Chats')}>
                <IconChat size={14} />
                {t('dashboard.tabs.chatsButton', 'Chats')}
              </button>
              <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                <button
                  type="button"
                  className={mobileTab === 'chat' ? 'btn primary sm' : 'btn sm'}
                  onClick={() => setMobileTab('chat')}
                >
                  <IconChat size={14} />
                  {t('dashboard.tabs.chat', 'Chat')}
                </button>
                <button
                  type="button"
                  className={mobileTab === 'assets' ? 'btn primary sm' : 'btn sm'}
                  onClick={() => setMobileTab('assets')}
                >
                  <IconFolder size={14} />
                  {t('dashboard.tabs.assets', 'Assets')}
                </button>
              </div>
            </div>

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
              {mobileTab === 'chat' ? (
                <section style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>
                  <ChatPanel
                    chats={chats}
                    onOpenDrawer={() => setIsVerboseDrawerOpen(true)}
                    onCancel={handleCancelProject}
                    isCancelling={isCancelling}
                    organizing={organizing}
                    hideSelector
                    projectId={id}
                    versions={versions}
                  />
                </section>
              ) : (
                <ProjectAssetsPanel projectId={id || ''} />
              )}
            </div>

            {chatDrawerOpen && (
              <div
                onClick={() => setChatDrawerOpen(false)}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 50, display: 'flex' }}
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{ width: '80%', maxWidth: 300, height: '100%', display: 'flex', flexDirection: 'column' }}
                >
                  <ChatRail
                    chats={chats.chats}
                    currentChatId={chats.currentChatId}
                    isDraftMode={chats.isDraftMode}
                    messagesByChat={chats.messagesByChat}
                    onSelect={(chatId) => {
                      chats.selectChat(chatId);
                      setMobileTab('chat');
                      setChatDrawerOpen(false);
                    }}
                    onBeginDraft={() => {
                      chats.beginDraftChat();
                      setMobileTab('chat');
                      setChatDrawerOpen(false);
                    }}
                    collapsed={false}
                    onToggle={() => setChatDrawerOpen(false)}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div
            style={{
              flex: 1,
              display: 'grid',
              gridTemplateColumns: `${chatsRailCollapsed ? '44px' : '220px'} 1.1fr 1fr`,
              gridTemplateRows: 'minmax(0, 1fr)',
              minHeight: 0,
              transition: 'grid-template-columns 0.2s ease',
            }}
          >
            <ChatRail
              chats={chats.chats}
              currentChatId={chats.currentChatId}
              isDraftMode={chats.isDraftMode}
              messagesByChat={chats.messagesByChat}
              onSelect={chats.selectChat}
              onBeginDraft={chats.beginDraftChat}
              collapsed={chatsRailCollapsed}
              onToggle={() => setChatsRailCollapsed((v) => !v)}
            />

            <section style={{ display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--line)', minWidth: 0, minHeight: 0 }}>
              <ChatPanel
                chats={chats}
                onOpenDrawer={() => setIsVerboseDrawerOpen(true)}
                onCancel={handleCancelProject}
                isCancelling={isCancelling}
                organizing={organizing}
                hideSelector
                projectId={id}
                versions={versions}
              />
            </section>

            <ProjectAssetsPanel projectId={id || ''} />
          </div>
        )}

        {/* Quiet usage footer */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            padding: isMobile ? '8px 14px' : '8px 28px',
            borderTop: '1px solid var(--line)',
            alignItems: 'center',
            gap: isMobile ? 12 : 20,
            background: 'var(--bg)',
            fontSize: 12,
            color: 'var(--text-5)',
          }}
        >
          <span style={{ color: 'var(--text-6)' }}>{t('dashboard.thisProject', 'This project')}</span>
          {project.creditsUsed != null && (
            <span>
              <span className="tnum" style={{ color: 'var(--text-2)' }}>
                {project.creditsUsed.toLocaleString()}
              </span>{' '}
              {t('dashboard.creditsUsed', 'credits used')}
            </span>
          )}
          {project.totalGeneratedImages != null && (
            <span>
              <span className="tnum" style={{ color: 'var(--text-2)' }}>
                {project.totalGeneratedImages.toLocaleString()}
              </span>{' '}
              {t('dashboard.imagesGenerated', 'images generated')}
            </span>
          )}
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="btn ghost sm icon"
            onClick={() => setIsVerboseDrawerOpen(true)}
            title={t('dashboard.detailLog', 'Detail log')}
          >
            <IconCog size={14} />
          </button>
        </div>
      </div>

      {showBuildsDialog && (
        <BuildsDialog
          projectId={id || ''}
          projectVersion={project.version}
          displayMode={displayMode}
          isChatRunning={chats.isAnyRunning}
          engineUpgradeRequired={engineUpgradeRequired}
          onUpgradeEngine={handleUpgradeEngine}
          builds={builds}
          buildsLoading={buildsLoading}
          refreshBuilds={refreshBuilds}
          onBuild={handleStartBuild}
          onClose={() => {
            setShowBuildsDialog(false);
            loadAvailablePlatforms();
          }}
          onGoToVersionControl={() => {
            setShowBuildsDialog(false);
            loadAvailablePlatforms();
            setMainTab('versions');
          }}
        />
      )}

      {showComingSoonPopup && (
        <ComingSoonPopup
          isOpen
          onClose={() => setShowComingSoonPopup(null)}
          title={showComingSoonPopup}
        />
      )}

      {showOutOfCreditsPopup && (
        <OutOfCreditsPopup type="credits" onClose={() => setShowOutOfCreditsPopup(false)} />
      )}

      {cancelNotice && (
        <div
          role="status"
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            fontSize: 13,
            color: 'var(--danger)',
            border: '1px solid var(--danger-line)',
            background: 'rgba(20,20,20,0.92)',
            padding: '10px 16px',
            borderRadius: 10,
            boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
          }}
        >
          {cancelNotice}
        </div>
      )}

      <VerboseDrawer
        open={isVerboseDrawerOpen}
        onClose={() => setIsVerboseDrawerOpen(false)}
        logs={chats.currentVerboseLogs}
      />
    </div>
  );
}
